// Unified dictation helper: Capacitor native plugin on iOS (TestFlight /
// App Store builds), webkitSpeechRecognition fallback in the PWA.
//
// Apple disables window.webkitSpeechRecognition inside WKWebView, so any
// Capacitor build must route through @capacitor-community/speech-recognition
// to reach iOS's native SFSpeechRecognizer. The helper exposes a single
// callback-based API so callers don't have to branch on platform.
import { Capacitor } from '@capacitor/core';

const isNative = Capacitor.isNativePlatform();

export function isDictationAvailable() {
  if (isNative) return true; // assume yes — runtime check happens at start()
  return typeof window !== "undefined" && !!(window.SpeechRecognition || window.webkitSpeechRecognition);
}

// Start a dictation session. Returns { stop } — call stop() to end early;
// the session will otherwise end when the user stops speaking.
//
// Callbacks:
//   onPartial(text)        — fired repeatedly as text comes in
//   onFinal(text)          — fired once with the final transcript
//   onError(msg)           — fired with a human-readable error
//   onEnd(reason)          — fired when the session ends (success or cancel)
//   onAudioLevel(level)    — fired ~20× per second with a 0…1 mic level
//                            (native only; web fallback never fires this)
// Best locale for the user's device. iOS SFSpeechRecognizer supports
// en-US, en-GB, en-ZA, etc. natively; honoring the device locale gives
// better recognition for accents and regional words ("biltong", "bakkie").
function defaultLocale() {
  try {
    if (typeof navigator !== "undefined" && navigator.language) return navigator.language;
  } catch {}
  return "en-US";
}

// Strip debug-only suffix (e.g. " [domain=kAFAssistantErrorDomain code=1110]")
// from error strings before showing to users. Keep the leading message.
export function sanitizeError(msg) {
  if (!msg) return "";
  return String(msg).replace(/\s*\[domain=[^\]]+\]\s*$/, "").trim();
}

// Detect permission-denied errors so the caller can render a CTA that opens
// iOS Settings directly to the app's privacy page.
export function isPermissionError(msg) {
  if (!msg) return false;
  return /permission|denied|access denied|not authorized|allow|enable in settings/i.test(String(msg));
}

// Best-effort jump to the iOS Settings → Sirat permission page. WKWebView
// routes the system 'app-settings:' URL natively. Safe no-op on web.
export function openIOSSettings() {
  if (typeof window === "undefined") return;
  try { window.location.href = "app-settings:"; } catch {}
}

export async function startDictation({ onPartial, onFinal, onError, onEnd, onAudioLevel, lang } = {}) {
  const resolvedLang = lang || defaultLocale();
  if (isNative) return startNative({ onPartial, onFinal, onError, onEnd, onAudioLevel, lang: resolvedLang });
  return startWeb({ onPartial, onFinal, onError, onEnd, lang: resolvedLang });
}

async function startNative({ onPartial, onFinal, onError, onEnd, onAudioLevel, lang }) {
  // Trace every native call via console.warn — DebugScreen captures these,
  // so a single screenshot tells us exactly which call rejected and why.
  const log = (...a) => { try { console.warn("[dict]", ...a); } catch {} };
  const errStr = (e) => {
    if (!e) return "(no error)";
    if (typeof e === "string") return e;
    try {
      return JSON.stringify({
        message: e.message, code: e.code, name: e.name,
        errorMessage: e.errorMessage, data: e.data,
        keys: Object.keys(e || {}),
      });
    } catch { return String(e); }
  };
  log("startNative: begin", { lang });

  let SpeechRecognition;
  try {
    ({ SpeechRecognition } = await import('@capacitor-community/speech-recognition'));
    log("plugin import OK", {
      hasAvailable: typeof SpeechRecognition?.available,
      hasStart: typeof SpeechRecognition?.start,
      hasCheckPerms: typeof SpeechRecognition?.checkPermissions,
    });
  } catch (e) {
    log("plugin import FAILED", errStr(e));
    onError?.("Speech recognition plugin unavailable. Update the app from TestFlight.");
    onEnd?.();
    return { stop: async () => {} };
  }

  // available() is a courtesy check — when it rejects (transient plugin
  // state, another app holding the mic) we used to hard-fail the session
  // with "Could not check speech recognition availability." That left
  // users stuck in IDLE even when start() would have worked. Now we only
  // bail when available() *explicitly* tells us no; transient rejects
  // log a warning and let the real start() call surface any actual error.
  try {
    const r = await SpeechRecognition.available();
    log("available() resolved", r);
    if (r && r.available === false) {
      onError?.("Speech recognition isn't available on this device.");
      onEnd?.();
      return { stop: async () => {} };
    }
  } catch (e) {
    log("available() REJECTED, proceeding to start() anyway", errStr(e));
  }

  let perm;
  try {
    perm = await SpeechRecognition.checkPermissions();
    log("checkPermissions()", perm);
    if (perm?.speechRecognition !== 'granted') {
      perm = await SpeechRecognition.requestPermissions();
      log("requestPermissions()", perm);
    }
  } catch (e) {
    log("permissions THREW", errStr(e));
    onError?.("Microphone & speech permission required. Enable in Settings → Sirat.");
    onEnd?.();
    return { stop: async () => {} };
  }
  if (perm?.speechRecognition !== 'granted') {
    log("permission not granted, aborting", perm);
    onError?.("Microphone & speech permission required. Enable in Settings → Sirat.");
    onEnd?.();
    return { stop: async () => {} };
  }

  // Defensively tear down any lingering session before starting a new one.
  try {
    const r = await SpeechRecognition.isListening();
    log("isListening()", r);
    if (r?.listening) {
      try { await SpeechRecognition.stop(); log("pre-stop OK"); } catch (e) { log("pre-stop THREW", errStr(e)); }
      await new Promise(r => setTimeout(r, 120));
    }
  } catch (e) { log("isListening THREW", errStr(e)); }
  try { await SpeechRecognition.removeAllListeners(); log("removeAllListeners OK"); } catch (e) { log("removeAllListeners THREW", errStr(e)); }

  // With partialResults=true the plugin's start() resolves the instant the
  // audio engine boots — that means "ready to listen," NOT "session ended."
  // The real end-of-session signal is the listeningState='stopped' event,
  // which fires when iOS times out OR when we explicitly stop. Anything that
  // calls finish() on the start() resolve will kill the session prematurely
  // (red flash, then white). The original code had that bug.
  let stopped = false;
  let userStopped = false;
  let speechSeen = false;
  let attempts = 0;
  // Up to MAX_RESTARTS quiet re-arms while waiting for the user to start
  // speaking. Once speechSeen flips true we never re-arm — the next stop
  // event ends the session, matching the natural "I finished my sentence"
  // behavior best-in-class chat apps have.
  const MAX_RESTARTS = 5;
  // JS-side end-of-utterance detection. iOS's native "no speech detected"
  // timeout is ~2-3s and unreliable across sessions, so we watch the live
  // audio level instead. Once the user has spoken (speechSeen) and the mic
  // has stayed below SILENCE_LEVEL for SILENCE_MS, we manually finish.
  const SILENCE_LEVEL = 0.08;   // RMS threshold; matches T2.1 meter's noise floor
  const SILENCE_MS    = 1400;   // pause length that ends an utterance
  let lastVoiceAtMs = Date.now();
  let silenceTimer = null;
  let partialL, stateL, levelL;
  const cleanup = () => {
    try { partialL?.remove?.(); } catch {}
    try { stateL?.remove?.(); } catch {}
    try { levelL?.remove?.(); } catch {}
    if (silenceTimer) { clearInterval(silenceTimer); silenceTimer = null; }
  };
  // reason values used by callers:
  //   "speech" — natural silence after the user spoke (auto-send candidate)
  //   "user"   — user tapped the mic again to cancel
  //   "error"  — surfaced error
  //   "giveup" — max restarts hit before speech
  const finish = (reason) => {
    if (stopped) return;
    stopped = true;
    cleanup();
    onEnd?.(reason || "other");
  };
  // "No speech detected" (kAFAssistantErrorDomain code 1110) is iOS's
  // natural end-of-turn after ~1.5s of silence — treat as benign and
  // re-arm so the user has time to start speaking.
  const isBenign = (msg) => /cancel|no speech detected|1110/i.test(msg || "");

  try {
    partialL = await SpeechRecognition.addListener('partialResults', (data) => {
      const m = data?.matches?.[0];
      log("partialResults event", { len: data?.matches?.length, sample: m?.slice?.(0, 80) });
      if (m) {
        speechSeen = true;
        // Each partial = recent voice activity. Push the silence baseline
        // forward so the watchdog doesn't fire mid-sentence.
        lastVoiceAtMs = Date.now();
        onPartial?.(m);
      }
    });
    // Audio level events come at ~20Hz while the engine is running. Always
    // subscribe so the JS-side silence detector below works; the optional
    // onAudioLevel callback (for meters) is forwarded too.
    try {
      levelL = await SpeechRecognition.addListener('audioLevel', (data) => {
        const lvl = Number(data?.level);
        if (!Number.isFinite(lvl)) return;
        if (lvl >= SILENCE_LEVEL) lastVoiceAtMs = Date.now();
        if (typeof onAudioLevel === "function") onAudioLevel(lvl);
      });
    } catch (e) { log("audioLevel listener THREW", errStr(e)); }
    stateL = await SpeechRecognition.addListener('listeningState', (data) => {
      log("listeningState event", data);
      if (data?.status !== 'stopped' || stopped) return;
      const errDetail = data.errorDetail || "";
      // User-initiated stop, OR genuine error → end the session.
      if (userStopped) { finish("user"); return; }
      if (errDetail && !isBenign(errDetail)) {
        onError?.(errDetail);
        finish("error");
        return;
      }
      // 60s hard limit on SFSpeechRecognizer — detect the retry/timeout
      // error codes and silently restart so long monologues keep working.
      // codes: 203 (Retry), 1101 (Service timeout), 209 (busy/recover).
      const isSegmentLimit = /code=(?:203|209|1101)\b/.test(errDetail);
      if (isSegmentLimit && speechSeen && !userStopped) {
        log("60s segment limit hit, auto-restarting", { errDetail });
        attempts = 0;          // reset the pre-speech grace counter for the new segment
        speechSeen = false;    // future silence in the new segment is treated normally
        setTimeout(() => { if (!stopped) tryStart(); }, 80);
        return;
      }
      // If the user already spoke, treat this as natural end-of-utterance.
      // No re-arm — best-in-class dictation auto-stops on trailing silence.
      if (speechSeen) {
        log("end-of-utterance after speechSeen, finishing");
        finish("speech");
        return;
      }
      // Pre-speech silent timeout — re-arm so user has time to start.
      if (attempts < MAX_RESTARTS) {
        log("auto-restart on benign stop (no speech yet)", { errDetail, attempts });
        setTimeout(() => { if (!stopped) tryStart(); }, 80);
        return;
      }
      log("max restarts hit before speech, giving up");
      finish("giveup");
    });
    log("listeners attached");
  } catch (e) { log("addListener THREW", errStr(e)); }

  const doStart = () => SpeechRecognition.start({
    language: lang,
    partialResults: true,
    popup: false,
    prompt: '',
  });

  const tryStart = async () => {
    if (stopped) return;
    attempts++;
    log("start() attempt", attempts);
    const t0 = Date.now();
    try {
      const res = await doStart();
      log("start() resolved", { dtMs: Date.now() - t0, res });
      // With partialResults=true, res has no matches — engine just started.
      // Stay alive; listeningState='stopped' will end us later.
      const m = res?.matches?.[0];
      if (m) { onFinal?.(m); finish(); }
    } catch (e) {
      const dtMs = Date.now() - t0;
      if (userStopped) { log("start() rejected after user stop", { dtMs }); finish(); return; }
      const msg = e?.message || String(e) || "Dictation failed.";
      log("start() REJECTED", { dtMs, msg, err: errStr(e) });
      if (/ongoing/i.test(msg)) {
        try { await SpeechRecognition.stop(); log("recover stop OK"); } catch (e2) { log("recover stop THREW", errStr(e2)); }
        await new Promise(r => setTimeout(r, 200));
        if (attempts < MAX_RESTARTS && !stopped) return tryStart();
        onError?.("Microphone busy. Close other apps using the mic and try again.");
        finish();
        return;
      }
      if (isBenign(msg)) {
        if (speechSeen) { finish("speech"); return; }
        if (attempts < MAX_RESTARTS && !stopped) {
          await new Promise(r => setTimeout(r, 80));
          return tryStart();
        }
        finish("giveup");
        return;
      }
      onError?.(msg);
      finish("error");
    }
  };

  tryStart();

  // Silence watchdog — only meaningful after the user has produced speech.
  // Once triggered, we ask the native side to stop; the listeningState
  // 'stopped' event then resolves with reason="speech" because speechSeen
  // is true. That path is shared with iOS's own end-of-utterance, so the
  // caller sees a single, consistent finish reason.
  silenceTimer = setInterval(() => {
    if (stopped) { if (silenceTimer) { clearInterval(silenceTimer); silenceTimer = null; } return; }
    if (!speechSeen) return;
    const sinceVoice = Date.now() - lastVoiceAtMs;
    if (sinceVoice >= SILENCE_MS) {
      log("JS silence watchdog firing finish(speech)", { sinceVoice });
      // Stop the recognizer. If iOS doesn't emit 'stopped' fast enough,
      // we still finish manually after a short safety delay.
      try { SpeechRecognition.stop(); } catch {}
      const safety = setTimeout(() => {
        if (!stopped) finish("speech");
      }, 350);
      if (silenceTimer) { clearInterval(silenceTimer); silenceTimer = null; }
      // The 'stopped' handler clears the safety timer indirectly by calling
      // finish before it; finish() also clears the level listener so no
      // more level events arrive.
    }
  }, 200);

  return {
    stop: async () => {
      userStopped = true;
      try { await SpeechRecognition.stop(); } catch {}
      // Don't call finish() here — let the listeningState='stopped' event
      // drive it. If iOS doesn't emit one (shouldn't happen), the listener
      // will be cleaned up when the next session starts.
      setTimeout(() => { if (!stopped) finish("user"); }, 300);
    },
  };
}

function startWeb({ onPartial, onFinal, onError, onEnd, lang }) {
  const SR = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
  if (!SR) {
    onError?.("Voice dictation isn't supported in this browser.");
    onEnd?.();
    return { stop: async () => {} };
  }

  let rec;
  try { rec = new SR(); }
  catch {
    onError?.("Could not start dictation.");
    onEnd?.();
    return { stop: async () => {} };
  }

  rec.lang = lang;
  rec.continuous = false;
  rec.interimResults = true;

  let finalSoFar = '';
  rec.onresult = (e) => {
    let interim = '';
    for (let i = 0; i < e.results.length; i++) {
      const r = e.results[i];
      if (r.isFinal) finalSoFar += r[0].transcript;
      else interim += r[0].transcript;
    }
    const live = (finalSoFar + ' ' + interim).trim();
    if (interim) onPartial?.(live);
  };
  rec.onerror = (e) => {
    if (e.error === "not-allowed") onError?.("Microphone permission denied. Allow it in Settings → Safari → Microphone.");
    else if (e.error === "no-speech") onError?.("No speech detected. Try again.");
    else onError?.(e.error || "Dictation failed.");
  };
  rec.onend = () => {
    if (finalSoFar) onFinal?.(finalSoFar.trim());
    onEnd?.();
  };

  navigator.mediaDevices?.getUserMedia({ audio: true })
    .then(() => { try { rec.start(); } catch { onError?.("Could not start dictation."); onEnd?.(); } })
    .catch(() => { onError?.("Microphone permission denied."); onEnd?.(); });

  return { stop: async () => { try { rec.stop(); } catch {} } };
}
